import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

// Validation schema for exchange car form
const exchangeCarFormSchema = z.object({
  // PersonOne - Car Information
  personOneCarBrand: z.string().min(1, 'Car brand is required'),
  personOneCarModel: z.string().min(1, 'Car model is required'),
  personOneCarYear: z.number().min(1900).max(new Date().getFullYear() + 1),
  personOneCarPlateNo: z.string().min(1, 'Plate number is required'),
  personOneCarChassisNo: z.string().optional(),
  personOneCarColor: z.string().min(1, 'Color is required'),
  
  // PersonOne - Personal Information
  personOneName: z.string().min(1, 'Name is required'),
  personOneIdNo: z.string().min(1, 'ID number is required'),
  personOneAddress: z.string().optional(),
  personOneContact: z.string().min(1, 'Contact is required'),
  
  // PersonTwo - Car Information
  personTwoCarBrand: z.string().min(1, 'Car brand is required'),
  personTwoCarModel: z.string().min(1, 'Car model is required'),
  personTwoCarYear: z.number().min(1900).max(new Date().getFullYear() + 1),
  personTwoCarPlateNo: z.string().min(1, 'Plate number is required'),
  personTwoCarChassisNo: z.string().optional(),
  personTwoCarColor: z.string().min(1, 'Color is required'),
  
  // PersonTwo - Personal Information
  personTwoName: z.string().min(1, 'Name is required'),
  personTwoIdNo: z.string().min(1, 'ID number is required'),
  personTwoAddress: z.string().optional(),
  personTwoContact: z.string().min(1, 'Contact is required'),
  
  // Exchange Information
  amountOwed: z.union([z.string(), z.number()]).transform((val): number => {
    if (val === '' || val === undefined || val === null) return 0;
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? 0 : num;
  }).pipe(z.number().min(0)),
  paidAmount: z.union([z.string(), z.number()]).transform((val): number => {
    if (val === '' || val === undefined || val === null) return 0;
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? 0 : num;
  }).pipe(z.number().min(0)),
  notes: z.string().optional()
});

type ExchangeCarFormData = {
  personOneCarBrand: string;
  personOneCarModel: string;
  personOneCarYear: number;
  personOneCarPlateNo: string;
  personOneCarChassisNo?: string;
  personOneCarColor: string;
  personOneName: string;
  personOneIdNo: string;
  personOneAddress?: string;
  personOneContact: string;
  personTwoCarBrand: string;
  personTwoCarModel: string;
  personTwoCarYear: number;
  personTwoCarPlateNo: string;
  personTwoCarChassisNo?: string;
  personTwoCarColor: string;
  personTwoName: string;
  personTwoIdNo: string;
  personTwoAddress?: string;
  personTwoContact: string;
  amountOwed: string | number;
  paidAmount: string | number;
  notes?: string;
};

interface ExchangeCar {
  id: number;
  personOneCarBrand: string;
  personOneCarModel: string;
  personOneCarYear: number;
  personOneCarPlateNo: string;
  personOneCarChassisNo?: string | null;
  personOneCarColor: string;
  personOneName: string;
  personOneIdNo: string;
  personOneAddress: string | null;
  personOneContact: string;
  personTwoCarBrand: string;
  personTwoCarModel: string;
  personTwoCarYear: number;
  personTwoCarPlateNo: string;
  personTwoCarChassisNo?: string | null;
  personTwoCarColor: string;
  personTwoName: string;
  personTwoIdNo: string;
  personTwoAddress: string | null;
  personTwoContact: string;
  amountOwed: number;
  paidAmount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function EditExchangeCar() {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentExchangeCar, setCurrentExchangeCar] = useState<ExchangeCar | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors
  } = useForm<ExchangeCarFormData>({
    resolver: zodResolver(exchangeCarFormSchema),
    defaultValues: {
      personOneCarBrand: '',
      personOneCarModel: '',
      personOneCarYear: new Date().getFullYear(),
      personOneCarPlateNo: '',
      personOneCarChassisNo: '',
      personOneCarColor: '',
      personOneName: '',
      personOneIdNo: '',
      personOneAddress: '',
      personOneContact: '',
      personTwoCarBrand: '',
      personTwoCarModel: '',
      personTwoCarYear: new Date().getFullYear(),
      personTwoCarPlateNo: '',
      personTwoCarChassisNo: '',
      personTwoCarColor: '',
      personTwoName: '',
      personTwoIdNo: '',
      personTwoAddress: '',
      personTwoContact: '',
      amountOwed: 0,
      paidAmount: 0,
      notes: ''
    }
  });

  useEffect(() => {
    const fetchExchangeCarData = async () => {
      if (id) {
        try {
          const exchangeCarData = await api.get(`/api/exchange-cars/${id}`);
          if (exchangeCarData) {
            setCurrentExchangeCar(exchangeCarData);
            
            // Populate form with exchange car data
            setValue('personOneCarBrand', exchangeCarData.personOneCarBrand);
            setValue('personOneCarModel', exchangeCarData.personOneCarModel);
            setValue('personOneCarYear', exchangeCarData.personOneCarYear);
            setValue('personOneCarPlateNo', exchangeCarData.personOneCarPlateNo);
            setValue('personOneCarChassisNo', exchangeCarData.personOneCarChassisNo ?? '');
            setValue('personOneCarColor', exchangeCarData.personOneCarColor);
            setValue('personOneName', exchangeCarData.personOneName);
            setValue('personOneIdNo', exchangeCarData.personOneIdNo);
            setValue('personOneAddress', exchangeCarData.personOneAddress || '');
            setValue('personOneContact', exchangeCarData.personOneContact);
            
            setValue('personTwoCarBrand', exchangeCarData.personTwoCarBrand);
            setValue('personTwoCarModel', exchangeCarData.personTwoCarModel);
            setValue('personTwoCarYear', exchangeCarData.personTwoCarYear);
            setValue('personTwoCarPlateNo', exchangeCarData.personTwoCarPlateNo);
            setValue('personTwoCarChassisNo', exchangeCarData.personTwoCarChassisNo ?? '');
            setValue('personTwoCarColor', exchangeCarData.personTwoCarColor);
            setValue('personTwoName', exchangeCarData.personTwoName);
            setValue('personTwoIdNo', exchangeCarData.personTwoIdNo);
            setValue('personTwoAddress', exchangeCarData.personTwoAddress || '');
            setValue('personTwoContact', exchangeCarData.personTwoContact);
            
            setValue('amountOwed', exchangeCarData.amountOwed || 0);
            setValue('paidAmount', exchangeCarData.paidAmount || 0);
            setValue('notes', exchangeCarData.notes || '');
          } else {
            console.error('Failed to fetch exchange car data');
            navigate('/exchange-cars');
          }
        } catch (error) {
          console.error('Error fetching exchange car data:', error);
          navigate('/exchange-cars');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchExchangeCarData();
  }, [id, navigate, setValue]);

  const onSubmit = async (data: ExchangeCarFormData) => {
    try {
      setSubmitting(true);
      
      // Clean phone numbers by removing spaces
      const cleanedData = {
        ...data,
        personOneContact: data.personOneContact?.replace(/\s/g, '') || data.personOneContact,
        personOneCarChassisNo: data.personOneCarChassisNo || undefined,
        personTwoCarChassisNo: data.personTwoCarChassisNo || undefined,
        personTwoContact: data.personTwoContact?.replace(/\s/g, '') || data.personTwoContact
      };

      // Update the exchange car record
      await api.put(`/api/exchange-cars/${id}`, cleanedData);
      navigate('/exchange-cars');
    } catch (error) {
      console.error('Error updating car exchange:', error);
      toast.error('Failed to update car exchange');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!currentExchangeCar) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">{t('exchangeCarDetail.notFound')}</p>
          <Link to="/exchange-cars">
            <Button variant="outline" className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('exchangeCarDetail.backToHistory')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Link to="/exchange-cars">
              <Button variant="outline" size="sm" className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('exchangeCarDetail.backToHistory')}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('exchangeCar.editTitle')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('exchangeCar.editSubtitle')} - Exchange ID: {id}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* PersonOne Section */}
            <div className="space-y-4 border-2 border-cyan-200 rounded-lg p-6 bg-cyan-50/50">
              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-cyan-300 pb-2">
                {t('exchangeCar.personOne')}
              </h3>
              
              {/* PersonOne Car Details */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.carDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarBrand">{t('exchangeCar.carBrand')} *</Label>
                    <Input
                      id="personOneCarBrand"
                      type="text"
                      placeholder="e.g., Toyota"
                      {...register('personOneCarBrand', {
                        onChange: () => clearErrors('personOneCarBrand')
                      })}
                      className={errors.personOneCarBrand ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarBrand && (
                      <p className="text-red-500 text-sm">{errors.personOneCarBrand.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarModel">{t('exchangeCar.carModel')} *</Label>
                    <Input
                      id="personOneCarModel"
                      type="text"
                      placeholder="e.g., Camry"
                      {...register('personOneCarModel', {
                        onChange: () => clearErrors('personOneCarModel')
                      })}
                      className={errors.personOneCarModel ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarModel && (
                      <p className="text-red-500 text-sm">{errors.personOneCarModel.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarYear">{t('exchangeCar.carYear')} *</Label>
                    <Input
                      id="personOneCarYear"
                      type="number"
                      placeholder="2024"
                      {...register('personOneCarYear', { 
                        valueAsNumber: true,
                        onChange: () => clearErrors('personOneCarYear')
                      })}
                      className={errors.personOneCarYear ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarYear && (
                      <p className="text-red-500 text-sm">{errors.personOneCarYear.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarPlateNo">{t('exchangeCar.plateNumber')} *</Label>
                    <Input
                      id="personOneCarPlateNo"
                      type="text"
                      placeholder="Plate number"
                      {...register('personOneCarPlateNo', {
                        onChange: () => clearErrors('personOneCarPlateNo')
                      })}
                      className={errors.personOneCarPlateNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarPlateNo && (
                      <p className="text-red-500 text-sm">{errors.personOneCarPlateNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarColor">{t('exchangeCar.carColor')} *</Label>
                    <Input
                      id="personOneCarColor"
                      type="text"
                      placeholder="e.g., Silver"
                      {...register('personOneCarColor', {
                        onChange: () => clearErrors('personOneCarColor')
                      })}
                      className={errors.personOneCarColor ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarColor && (
                      <p className="text-red-500 text-sm">{errors.personOneCarColor.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarChassisNo">{t('exchangeCar.chassisNo')}</Label>
                    <Input
                      id="personOneCarChassisNo"
                      type="text"
                      placeholder="Chassis number (optional)"
                      {...register('personOneCarChassisNo')}
                    />
                  </div>
                </div>
              </div>

              {/* PersonOne Personal Details */}
              <div className="space-y-4 mt-6">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.personalDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personOneName">{t('exchangeCar.name')} *</Label>
                    <Input
                      id="personOneName"
                      type="text"
                      placeholder="Full name"
                      {...register('personOneName', {
                        onChange: () => clearErrors('personOneName')
                      })}
                      className={errors.personOneName ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneName && (
                      <p className="text-red-500 text-sm">{errors.personOneName.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneIdNo">{t('exchangeCar.idNo')} *</Label>
                    <Input
                      id="personOneIdNo"
                      type="text"
                      placeholder="ID number"
                      {...register('personOneIdNo', {
                        onChange: () => clearErrors('personOneIdNo')
                      })}
                      className={errors.personOneIdNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneIdNo && (
                      <p className="text-red-500 text-sm">{errors.personOneIdNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneAddress">{t('exchangeCar.address')}</Label>
                    <Input
                      id="personOneAddress"
                      type="text"
                      placeholder="Address (optional)"
                      {...register('personOneAddress', {
                        onChange: () => clearErrors('personOneAddress')
                      })}
                      className={errors.personOneAddress ? 'border-red-500 focus:border-red-500' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneContact">{t('exchangeCar.contact')} *</Label>
                    <Input
                      id="personOneContact"
                      type="text"
                      placeholder="Contact number"
                      {...register('personOneContact', {
                        onChange: () => clearErrors('personOneContact')
                      })}
                      className={errors.personOneContact ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneContact && (
                      <p className="text-red-500 text-sm">{errors.personOneContact.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* PersonTwo Section */}
            <div className="space-y-4 border-2 border-emerald-200 rounded-lg p-6 bg-emerald-50/50">
              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-emerald-300 pb-2">
                {t('exchangeCar.personTwo')}
              </h3>
              
              {/* PersonTwo Car Details */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.carDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarBrand">{t('exchangeCar.carBrand')} *</Label>
                    <Input
                      id="personTwoCarBrand"
                      type="text"
                      placeholder="e.g., Honda"
                      {...register('personTwoCarBrand', {
                        onChange: () => clearErrors('personTwoCarBrand')
                      })}
                      className={errors.personTwoCarBrand ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarBrand && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarBrand.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarModel">{t('exchangeCar.carModel')} *</Label>
                    <Input
                      id="personTwoCarModel"
                      type="text"
                      placeholder="e.g., Accord"
                      {...register('personTwoCarModel', {
                        onChange: () => clearErrors('personTwoCarModel')
                      })}
                      className={errors.personTwoCarModel ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarModel && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarModel.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarYear">{t('exchangeCar.carYear')} *</Label>
                    <Input
                      id="personTwoCarYear"
                      type="number"
                      placeholder="2024"
                      {...register('personTwoCarYear', { 
                        valueAsNumber: true,
                        onChange: () => clearErrors('personTwoCarYear')
                      })}
                      className={errors.personTwoCarYear ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarYear && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarYear.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarPlateNo">{t('exchangeCar.plateNumber')} *</Label>
                    <Input
                      id="personTwoCarPlateNo"
                      type="text"
                      placeholder="Plate number"
                      {...register('personTwoCarPlateNo', {
                        onChange: () => clearErrors('personTwoCarPlateNo')
                      })}
                      className={errors.personTwoCarPlateNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarPlateNo && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarPlateNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarColor">{t('exchangeCar.carColor')} *</Label>
                    <Input
                      id="personTwoCarColor"
                      type="text"
                      placeholder="e.g., Black"
                      {...register('personTwoCarColor', {
                        onChange: () => clearErrors('personTwoCarColor')
                      })}
                      className={errors.personTwoCarColor ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarColor && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarColor.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarChassisNo">{t('exchangeCar.chassisNo')}</Label>
                    <Input
                      id="personTwoCarChassisNo"
                      type="text"
                      placeholder="Chassis number (optional)"
                      {...register('personTwoCarChassisNo')}
                    />
                  </div>
                </div>
              </div>

              {/* PersonTwo Personal Details */}
              <div className="space-y-4 mt-6">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.personalDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personTwoName">{t('exchangeCar.name')} *</Label>
                    <Input
                      id="personTwoName"
                      type="text"
                      placeholder="Full name"
                      {...register('personTwoName', {
                        onChange: () => clearErrors('personTwoName')
                      })}
                      className={errors.personTwoName ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoName && (
                      <p className="text-red-500 text-sm">{errors.personTwoName.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoIdNo">{t('exchangeCar.idNo')} *</Label>
                    <Input
                      id="personTwoIdNo"
                      type="text"
                      placeholder="ID number"
                      {...register('personTwoIdNo', {
                        onChange: () => clearErrors('personTwoIdNo')
                      })}
                      className={errors.personTwoIdNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoIdNo && (
                      <p className="text-red-500 text-sm">{errors.personTwoIdNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoAddress">{t('exchangeCar.address')}</Label>
                    <Input
                      id="personTwoAddress"
                      type="text"
                      placeholder="Address (optional)"
                      {...register('personTwoAddress', {
                        onChange: () => clearErrors('personTwoAddress')
                      })}
                      className={errors.personTwoAddress ? 'border-red-500 focus:border-red-500' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoContact">{t('exchangeCar.contact')} *</Label>
                    <Input
                      id="personTwoContact"
                      type="text"
                      placeholder="Contact number"
                      {...register('personTwoContact', {
                        onChange: () => clearErrors('personTwoContact')
                      })}
                      className={errors.personTwoContact ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoContact && (
                      <p className="text-red-500 text-sm">{errors.personTwoContact.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Exchange Information */}
            <div className="space-y-4 border-2 border-teal-200 rounded-lg p-6 bg-teal-50/50">
              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-teal-300 pb-2">
                {t('exchangeCar.exchangeInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="amountOwed">{t('exchangeCar.amountOwed')}</Label>
                  <Input
                    id="amountOwed"
                    type="number"
                    placeholder="0"
                    step="0.01"
                    {...register('amountOwed', { 
                      valueAsNumber: true,
                      onChange: () => clearErrors('amountOwed')
                    })}
                    className={errors.amountOwed ? 'border-red-500 focus:border-red-500' : ''}
                  />
                  <p className="text-sm text-gray-600">{t('exchangeCar.amountOwedHint')}</p>
                  {errors.amountOwed && (
                    <p className="text-red-500 text-sm">{errors.amountOwed.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paidAmount">{t('exchangeCar.paidAmount')}</Label>
                  <Input
                    id="paidAmount"
                    type="number"
                    placeholder="0"
                    step="0.01"
                    {...register('paidAmount', { 
                      valueAsNumber: true,
                      onChange: () => clearErrors('paidAmount')
                    })}
                    className={errors.paidAmount ? 'border-red-500 focus:border-red-500' : ''}
                  />
                  <p className="text-sm text-gray-600">{t('exchangeCar.paidAmountHint')}</p>
                  {errors.paidAmount && (
                    <p className="text-red-500 text-sm">{errors.paidAmount.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">{t('exchangeCar.notes')}</Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="Additional notes (optional)"
                    {...register('notes', {
                      onChange: () => clearErrors('notes')
                    })}
                    className={errors.notes ? 'border-red-500 focus:border-red-500' : ''}
                  />
                </div>
              </div>
            </div>
              
            <div className="flex justify-center gap-4">
              <Button 
                type="submit" 
                disabled={isSubmitting || submitting}
                className="w-full md:w-auto bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white font-semibold px-8 py-3"
              >
                {isSubmitting || submitting ? t('exchangeCar.updating') : t('exchangeCar.updateCar')}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

